import { useState, useContext, useMemo, useCallback } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import {
    IFile,
    IFileError,
    UploadFieldWithServices,
    UploadServiceProvider,
    UploadButtonWithContext
} from '@flos/react-ui';
import useAppState from '../state/appState';
import { item } from 'c9-tracking';
import { trackInfo } from '../track-by-submit/common/tracking';

interface IFileUpload {
    readonly taskId: string;
    c9uploadApiKey: string;
    handleUploadedFiles: (files: IFile[]) => void;
    handleIsFileProcessing: (status: boolean) => void;
    handleFileDeleted: (files: IFile[]) => void;
}

const FileUploadWrapper = ({
    taskId,
    c9uploadApiKey,
    handleIsFileProcessing,
    handleUploadedFiles,
    handleFileDeleted
}: IFileUpload) => {
    const formConfig = useAppState((state) => state.formConfig);
    const { appName, environment, endpointConfig, uploadClientId } = useContext(ConfigContext);

    const [uploadErrorsMessages, setUploadErrorsMessages] = useState<string[]>([]);

    // use useMemo hook to cache config to prevent infinite loop between Provider config change and onScan callback.
    const uploadServiceConfig = useMemo(
        () => ({
            apiBaseUrl: endpointConfig.apiAwsBase,
            appname: appName,
            clientId: uploadClientId,
            taskId: taskId,
            minUploadFileSize: 45,
            maxUploadFileSizeInMb: 50,
            totalFilesLimit: 50,
            metaData: { environment, securitylevel: 'IKKESENSITIV', owner: 'SKADESFORSIKRING-SKADE' }
        }),
        [endpointConfig.apiAwsBase, appName, uploadClientId, taskId, environment]
    );

    const handleError = useCallback(
        (errors: IFileError[]) => {
            const trackUploadErrorsList: item[][] = [];
            const messageList: string[] = [];

            errors.forEach((err) => {
                const toTrack: item[] = [];
                const fileName = err.fileName ?? '';
                const fileNameText = `${fileName ? ` "${fileName}" ` : ' '}`;
                switch (err.error) {
                    case 'FILE_TOO_SMALL':
                        if (err.metaData && err.metaData.minUploadFileSize) {
                            messageList.push(
                                `Filen${fileNameText}blev ikke uploadet. Filen er for lille - mindst ${
                                    err.metaData.minUploadFileSize as number
                                } byte.`
                            );
                        } else {
                            messageList.push(`Filen${fileNameText}blev ikke uploadet. Filen er for lille.`);
                        }
                        toTrack.push({ key: 'claim-form-upload-error', value: 'less than min file size' });
                        break;
                    case 'FILE_TOO_LARGE':
                        if (err.metaData && err.metaData.maxUploadFileSizeInMb) {
                            messageList.push(
                                `Filen${fileNameText}blev ikke uploadet. Filen er for stor - højst ${
                                    err.metaData.maxUploadFileSizeInMb as number
                                } MB.`
                            );
                        } else {
                            messageList.push(`Filen${fileNameText}blev ikke uploadet. Filen er for stor.`);
                        }
                        toTrack.push({ key: 'claim-form-upload-error', value: 'greater than max file size' });
                        break;
                    case 'FILE_IS_INFECTED':
                        messageList.push(`Filen${fileNameText}blev ikke uploadet. Filen er inficeret`);
                        toTrack.push({ key: 'claim-form-upload-error', value: 'file is infected' });
                        break;
                    case 'FILE_INVALID_TYPE':
                        messageList.push(`Filen${fileNameText}blev ikke uploadet. Filtypen er ikke tilladt`);
                        toTrack.push({ key: 'claim-form-upload-error', value: 'invalid file type' });
                        const fileExtension = getFileExtension(fileName);
                        if (fileExtension) {
                            toTrack.push({
                                key: 'claim-form-upload-error-extension',
                                value: fileExtension ?? 'no extension'
                            });
                        }
                        break;
                    case 'TOO_MANY_FILES':
                        if (err.metaData && err.metaData.totalFilesLimit) {
                            messageList.push(
                                `Filen${fileNameText}blev ikke uploadet. Du kan uploade max. ${
                                    err.metaData.totalFilesLimit as number
                                } filer i alt.`
                            );
                        } else {
                            messageList.push(
                                `Filen${fileNameText}blev ikke uploadet. Der kan ikke uploades flere filer.`
                            );
                        }
                        toTrack.push({ key: 'claim-form-upload-error', value: 'more than max allowed files' });
                        break;
                    case 'SCANNING_ERROR':
                        messageList.push(`Filen${fileNameText}blev ikke uploadet.`);
                        toTrack.push({ key: 'claim-form-upload-error', value: 'scanning error' });
                        break;
                    case 'CONFIG_ERROR':
                        messageList.push(
                            'Der er desværre sket en fejl. Prøv igen. Kontakt os, hvis problemet fortsætter.'
                        );
                        toTrack.push({ key: 'claim-form-upload-error', value: 'config error' });
                        break;
                    case 'DELETING_ERROR':
                        messageList.push(`Filen${fileNameText}blev ikke slettet.`);
                        toTrack.push({ key: 'claim-form-upload-error', value: 'deleting error' });
                        break;
                    case 'UPLOAD_ERROR':
                        messageList.push(`Filen${fileNameText}blev ikke uploadet.`);
                        toTrack.push({ key: 'claim-form-upload-error', value: 'upload error' });
                        break;
                    case 'STATE_ERROR':
                        messageList.push(
                            `Der er desværre sket en fejl. Prøv igen. Kontakt os, hvis problemet fortsætter.`
                        );
                        toTrack.push({ key: 'claim-form-upload-error', value: 'state error' });
                        break;
                    case 'FILE_ALREADY_EXISTS':
                        messageList.push(`Filnavnet${fileNameText}eksisterer allerede - omdøb filnavn`);
                        toTrack.push({ key: 'claim-form-upload-error', value: 'file already exists' });
                        break;
                    case 'FILE_NAME_TOO_LONG':
                        messageList.push('Filnavnet er for langt - maks. 75 karakterer');
                        toTrack.push({ key: 'claim-form-upload-error', value: 'file name too long' });
                        break;
                    case 'FILE_NAME_FORBIDDEN_CHARACTERS':
                        messageList.push(
                            'Filnavnet indeholder en eller flere ugyldige karakterer: {offendingCharacters}. Omdøb filnavn'.replace(
                                '{offendingCharacters}',
                                `${err.metaData?.offendingCharacters}`
                            )
                        );
                        toTrack.push({ key: 'claim-form-upload-error', value: 'forbidden characters' });
                        break;
                    default:
                        messageList.push(
                            `Der er desværre sket en fejl. Prøv igen. Kontakt os, hvis problemet fortsætter.`
                        );
                        toTrack.push({ key: 'claim-form-upload-error', value: `${err.error}` });
                        break;
                }

                trackUploadErrorsList.push(toTrack);
            });
            setUploadErrorsMessages(messageList);

            for (const error of trackUploadErrorsList) {
                if (formConfig) {
                    trackInfo(formConfig, error);
                }
            }
        },
        [formConfig]
    );

    const handleFileCount = (count: number) => {
        return count === 1 ? `Tilføjet 1 fil` : `Tilføjet ${count} filer`;
    };

    const handleFileScanned = (files: IFile[]) => {
        const scanning = files.some((file) => file.scanStatus === 'pending' || file.scanStatus === 'scanning');
        const scanned = files.filter((file) => file.scanStatus === 'clean');
        handleIsFileProcessing(scanning);
        handleUploadedFiles(scanned);
    };

    const handleFileUpload = (files: IFile[], loading: boolean) => {
        handleIsFileProcessing(loading);
    };

    const handleFileDelete = (filename: string, files: IFile[]) => {
        handleFileDeleted(files);
    };

    return (
        <div className={uploadErrorsMessages.length > 0 ? 'has-error' : ''}>
            <UploadServiceProvider config={uploadServiceConfig} key={c9uploadApiKey}>
                <UploadFieldWithServices
                    deleteButtonLabel={'Fjern'}
                    description={
                        'Du kan tilføje filer af typen bmp, doc/docx, gif, jpg/jpeg, png, pdf, rtf, tif, txt, xls/xlsx på højst 50 MB.'
                    }
                    onScan={handleFileScanned}
                    onError={handleError}
                    onFileCount={handleFileCount}
                    errorMessages={uploadErrorsMessages}
                    errorLabel="[Der er desværre sket en teknisk fejl]"
                    infectedLabel="[Filen er inficeret og kan desværre ikke uploades]"
                    onDelete={handleFileDelete}
                    onUpload={handleFileUpload}
                >
                    <UploadButtonWithContext>Tilføj filer</UploadButtonWithContext>
                </UploadFieldWithServices>
            </UploadServiceProvider>
            <br />
        </div>
    );
};

const getFileExtension = (filename: string): string | null => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop()! : null;
};

export default FileUploadWrapper;
